import { format } from 'date-fns';
import type { ILocationEventsResponseBody } from '../../../types/response/ILocationEventsResponseBody';
import { SimpleArtist } from './SimpleArtist';
import { SimpleMember } from './SimpleMember';

export class BaseLocationEvent {
  constructor(
    private readonly _eventId: string,
    private readonly _name: string,
    private readonly _locationId: string,
    private readonly _locationIds: string[],
    private readonly _startDate: Date,
    private readonly _endDate: Date,
    private readonly _category: string,
    private readonly _status: string,
    private readonly _countryEmoji: string,
    private readonly _countryName: string,
    private readonly _goodsNames: string[] | null,
    private readonly _artist: SimpleArtist,
    private readonly _members: SimpleMember[],
    private readonly _baseImageUrl: string,
    private readonly _organizerTwitterId: string,
    private readonly _description: string | null
  ) {}

  get eventId(): string {
    return this._eventId;
  }

  get name(): string {
    return this._name;
  }

  get locationId(): string {
    return this._locationId;
  }

  get locationIds(): string[] {
    return this._locationIds;
  }

  get startDate(): Date {
    return this._startDate;
  }

  get startDateString(): string {
    return this._startDate.toDateString();
  }

  formatedStartDateString(dateFormat: string): string {
    return format(this._startDate, dateFormat);
  }

  get endDate(): Date {
    return this._endDate;
  }

  get endDateString(): string {
    return this._endDate.toDateString();
  }

  formatedEndDateString(dateFormat: string): string {
    return format(this._endDate, dateFormat);
  }

  get category(): string {
    return this._category;
  }

  get status(): string {
    return this._status;
  }

  get countryEmoji(): string {
    return this._countryEmoji;
  }

  get countryName(): string {
    return this._countryName;
  }

  get goodsNames(): string[] | null {
    return this._goodsNames;
  }

  get artist(): SimpleArtist {
    return this._artist;
  }

  get members(): SimpleMember[] {
    return this._members;
  }

  get baseImageUrl(): string {
    return this._baseImageUrl;
  }

  get organizerTwitterId(): string {
    return this._organizerTwitterId?.replace('@', '');
  }

  get description(): string | null {
    return this._description;
  }

  public isOngoing(): boolean {
    const now = new Date();
    return now >= this._startDate && now <= this._endDate;
  }

  public isUpcoming(): boolean {
    const now = new Date();
    return now < this._startDate;
  }

  public isPast(): boolean {
    const now = new Date();
    return now > this._endDate;
  }

  public getDurationInDays(): number {
    return Math.ceil((this._endDate.getTime() - this._startDate.getTime()) / (1000 * 60 * 60 * 24));
  }

  public static fromResponse(response: ILocationEventsResponseBody): BaseLocationEvent {
    return new BaseLocationEvent(
      response.eventId,
      response.name,
      response.locationId,
      response.locationIds,
      new Date(response.startDate),
      new Date(response.endDate),
      response.category,
      response.status,
      response.countryEmoji,
      response.countryName,
      response.goodsNames,
      SimpleArtist.fromResponse(response.artist),
      response.members.map((member) => SimpleMember.fromResponse(member)),
      response.baseImageUrl,
      response.organizerTwitterId,
      response.description
    );
  }
}
